// src/components/NewListingsRow.jsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { FALLBACK_IMG } from '../utils/image';
import './NewListingsRow.css';

const NEW_LISTING_DAYS = 7;

// ---------- helpers ----------
function getEffectivePricePi(p, piToUsdRate) {
  if (p?.pricingView && typeof p.pricingView.effectivePrice === 'number') {
    const base = p.pricingView.basePrice;
    const eff = p.pricingView.effectivePrice;
    const isOnSale = !!p.pricingView.isOnFlashSale;

    const percent =
      isOnSale && typeof base === 'number' && base > 0
        ? Math.max(0, Math.min(100, Math.round((1 - eff / base) * 100)))
        : null;

    return { pricePi: eff, isOnSale, percentOff: percent };
  }

  if (typeof p?.effectivePricePi === 'number') {
    const base =
      p?.pricingType === 'dynamic' &&
      typeof p?.usdPrice === 'number' &&
      typeof piToUsdRate === 'number' &&
      piToUsdRate > 0
        ? p.usdPrice / piToUsdRate
        : typeof p?.price === 'number'
        ? p.price
        : null;

    const eff = p.effectivePricePi;
    const isOnSale = !!(p?.sale?.active || p?.isOnSale === true);

    const percent =
      isOnSale && typeof base === 'number' && base > 0
        ? Math.max(0, Math.min(100, Math.round((1 - eff / base) * 100)))
        : null;

    return { pricePi: eff, isOnSale, percentOff: percent };
  }

  const isDynamic =
    p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';

  const basePi =
    isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? p.usdPrice / piToUsdRate
      : typeof p?.price === 'number'
      ? p.price
      : null;

  const sale = p?.sale || p?.flashSale || p?.activeSale || {};

  const saleActive =
    !!sale?.active ||
    typeof p?.activeSalePricePi === 'number' ||
    typeof p?.activeSalePriceUsd === 'number' ||
    typeof p?.salePrice === 'number' ||
    typeof p?.salePricePi === 'number' ||
    typeof p?.salePriceUsd === 'number';

  let salePi = null;

  if (typeof sale?.pricePi === 'number') salePi = sale.pricePi;
  else if (typeof p?.activeSalePricePi === 'number') salePi = p.activeSalePricePi;
  else if (typeof p?.salePricePi === 'number') salePi = p.salePricePi;

  const usdToPi = (usd) =>
    typeof usd === 'number' &&
    typeof piToUsdRate === 'number' &&
    piToUsdRate > 0
      ? usd / piToUsdRate
      : null;

  if (salePi == null) {
    if (typeof sale?.priceUsd === 'number') salePi = usdToPi(sale.priceUsd);
    else if (typeof p?.activeSalePriceUsd === 'number') salePi = usdToPi(p.activeSalePriceUsd);
    else if (typeof p?.salePriceUsd === 'number') salePi = usdToPi(p.salePriceUsd);
  }

  if (salePi == null && typeof p?.salePrice === 'number') {
    salePi = p.salePrice;
  }

  if (basePi == null) {
    return { pricePi: null, isOnSale: false, percentOff: null };
  }

  if (!saleActive || salePi == null) {
    return { pricePi: basePi, isOnSale: false, percentOff: null };
  }

  const finalSalePi = Math.max(0, Number(Number(salePi).toFixed(2)));

  const percentOff = Math.max(
    0,
    Math.min(100, Math.round((1 - finalSalePi / basePi) * 100))
  );

  return {
    pricePi: finalSalePi,
    isOnSale: true,
    percentOff: Number.isFinite(percentOff) ? percentOff : null,
  };
}

function firstImage(p) {
  const src = Array.isArray(p?.media) && p.media[0] ? p.media[0] : null;
  return src || FALLBACK_IMG;
}

function titleOf(p) {
  return p?.name || p?.title || 'Untitled';
}

function isWithinLastDays(dateValue, days) {
  if (!dateValue) return false;

  const createdTime = new Date(dateValue).getTime();
  if (!Number.isFinite(createdTime)) return false;

  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return createdTime >= cutoff;
}
// ---------------------------------------------------------------

export default function NewListingsRow({ user, onOpen, onViewAll }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [piToUsdRate, setPiToUsdRate] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchRate = async () => {
      try {
        const res = await api.get('/convert/rate');

        if (!cancelled && res.data?.piToUsd) {
          setPiToUsdRate(res.data.piToUsd);
        }
      } catch (err) {
        console.error('❌ Failed to fetch cached Pi rate (new listings):', err?.message || err);
      }
    };

    fetchRate();

    const timer = setInterval(fetchRate, 600000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchNewListings = async () => {
      try {
        setLoading(true);

        const res = await api.get('/banners/recent/products', {
          params: {
            page: 1,
            limit: 10,
            username: user?.username || '',
          },
        });

        if (cancelled) return;

        const items = Array.isArray(res.data?.products)
          ? res.data.products.slice(0, 10)
          : [];

        const freshItems = items.filter((p) =>
          isWithinLastDays(p?.createdAt, NEW_LISTING_DAYS)
        );

        // Keep the UI clean. Do not render if there are fewer than 2 fresh products.
        if (freshItems.length < 2) {
          setProducts([]);
          return;
        }

        setProducts(freshItems);
      } catch (err) {
        console.error('❌ Failed to fetch new listings row:', err?.message || err);
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNewListings();

    return () => {
      cancelled = true;
    };
  }, [user?.username]);

  const visibleProducts = useMemo(() => {
    return Array.isArray(products) ? products.slice(0, 10) : [];
  }, [products]);

  const priceLabel = (p) => {
    const { pricePi } = getEffectivePricePi(p, piToUsdRate);
    return pricePi != null ? `@ ${pricePi.toFixed(2)} Pi` : '@ —';
  };

  const saleBadgeText = (p) => {
    const { isOnSale, percentOff } = getEffectivePricePi(p, piToUsdRate);
    if (!isOnSale) return null;

    return typeof percentOff === 'number' && percentOff > 0
      ? `-${percentOff}%`
      : '-SALE';
  };

  if (loading && visibleProducts.length === 0) {
    return null;
  }

  if (visibleProducts.length < 2) {
    return null;
  }

  return (
    <section className="newlistings" aria-label="New listings">
      <div className="newlistings__header">
        <div>
          <h3 className="newlistings__title">New Listings</h3>
          <p className="newlistings__subtitle">Fresh products from the marketplace</p>
        </div>

        <button
          type="button"
          className="newlistings__viewall"
          onClick={onViewAll}
        >
          View all
        </button>
      </div>

      <div className="newlistings__scroller" role="list">
        {visibleProducts.map((p) => {
          const src = firstImage(p);
          const title = titleOf(p);
          const badge = saleBadgeText(p);

          return (
            <button
              key={p._id}
              className="newlistings-card"
              role="listitem"
              onClick={() => onOpen?.(p)}
              aria-label={`View ${title}`}
              title={title}
            >
              <div className="newlistings-card__imgwrap">
                <img
                  className="newlistings-card__img"
                  src={src}
                  alt={title}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_IMG;
                  }}
                />

                {badge && (
                  <span className="newlistings-chip newlistings-chip--sale">
                    {badge}
                  </span>
                )}

                <span className="newlistings-chip newlistings-chip--new">
                  New
                </span>
              </div>

              <div className="newlistings-card__name">{title}</div>

              <div className="newlistings-card__meta">
                <div className="newlistings-card__price">{priceLabel(p)}</div>

                {p?.expectedDeliveryDays && (
                  <div className="newlistings-card__delivery">
                    {p.expectedDeliveryDays}d
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
